// Utils
import http from '../utils/services/http';
import { getDeviceId } from '../device/deviceService';
import globalLogger from '../logger/logger';

// Services
import { getCurrentUserToken, removeRememberedBetterAuthSessionUser, removeCurrentUserToken } from './authService';
import {
    getBetterAuthSessionMigrationStatus,
    resetBetterAuthSessionMigrationStatus,
} from './betterAuthSessionMigrationService';
import { clearCanvasReturnState } from '../canvas/canvasReturnStateService';
import { clearInboxReturnState } from '../inbox/returnState/inboxReturnStateService';
import { clearOnboardingProgressStep } from '../workspace/onboarding/config/onboardingProgressService';
import { clearDrawingLocalStorage } from '../element/drawing/drawingEditor/utils/drawingLocalStorageUtils';
import { clearMobileWorkspaceRestoreMode } from '../mobile/utils/mobileWorkspaceRestoreMode';
import { clearCheckoutState } from '../user/account/checkout/checkoutUtils';
import { removeLocalLastNavigationId } from '../user/navigationHistory/localNavigationHistoryService';

// Selectors
import { getCurrentUserId } from '../user/currentUserSelector';

// Constants
import { METHODS } from '../../common/utils/http/httpConstants';
import { ERROR_MESSAGES } from '../../common/error/errorConstants';
import * as AUTH_ACTION_TYPES from './authConstants';
import { openErrorNotice } from '../components/error/errorNoticeActions';

const logger = globalLogger.createChannel('auth.logout');

export const clearLegacyUserSession = () =>
    http({
        method: METHODS.GET,
        url: 'auth/clear-session',
    });

export const clearBetterAuthUserSession = () =>
    http({
        method: METHODS.POST,
        url: 'auth-v2/sign-out',
        skipAuthHeader: true,
        withCredentials: true,
    });

const shouldClearBetterAuthUserSession = (state: any): boolean => {
    const migrationStatus = getBetterAuthSessionMigrationStatus();

    if (migrationStatus === 'upgraded') return true;

    const hasLegacyToken = !!getCurrentUserToken();
    const isAuthenticated = !!state.getIn(['app', 'currentUser', 'authenticated']);

    return isAuthenticated && !hasLegacyToken;
};

const clearLastNavigatedBoard = () => removeLocalLastNavigationId();

// FIXME - Instead of performing this logic here, potentially move it to middleware?
//  Try to fix this so that the notifications can be unsubscribed from without causing
//  the electron build to fail
/**
 * Same as logout in authActions however this doesn't call unsubscribeFromPushNotifications
 * because that was causing the electron app to fail building.
 */
export const simpleLogout = () => async (dispatch: Function, getState: Function) => {
    const state = getState();
    const userId = getCurrentUserId(state);
    const shouldClearBetterAuthSession = shouldClearBetterAuthUserSession(state);

    try {
        await clearLegacyUserSession();
    } catch (error) {
        logger.warn('Failed to clear legacy auth session during logout', error);
    }

    if (shouldClearBetterAuthSession) {
        try {
            await clearBetterAuthUserSession();
        } catch (error) {
            logger.warn('Failed to clear Better Auth session during logout', error);
            dispatch(openErrorNotice({ errorNoticeId: ERROR_MESSAGES.NETWORK_ERROR }));
            return;
        }
    }

    clearMobileWorkspaceRestoreMode();
    clearOnboardingProgressStep();
    clearDrawingLocalStorage();
    clearCanvasReturnState();
    clearCheckoutState();
    clearInboxReturnState();
    clearLastNavigatedBoard();
    resetBetterAuthSessionMigrationStatus();
    removeCurrentUserToken();
    removeRememberedBetterAuthSessionUser();

    dispatch({
        type: AUTH_ACTION_TYPES.LOGOUT,
        deviceId: getDeviceId(),
        userId,
        sync: true,
    });
};
